// @flow
import React, { useEffect } from "react";
import {
    unstable_HistoryRouter as HistoryRouter,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { useAuth } from "./providers/CustomAuthProvider";
import { SnackbarProvider } from "notistack";
import history from "./utils/history";
import "./assets/scss/Saas.scss";
import "./assets/scss/Creative.scss";
import "@enerex/unified-ui-assets/css";
import "./assets/scss/custom/pages/_common.scss";
import PrivateRoute from "./routes/PrivateRoute";
import JsonSnackbar from "components/JsonSnackbar";
import UnifiedAppLayout from "./layouts/UnifiedAppLayout";
import LogoutPage from "./pages/account/Logout";
import licensesRawData from "./pages/license/licensesRawData";
import offersRawData from "./pages/offers/offersRawData";
import DashboardPage from "./pages/dashboard/dashboard";
import Loader from "./components/Loader";
import LicensedCompanies from "./pages/license/licenseFlow";
import CompaniesData from "./pages/companies/companiesData";
import CompaniesDetailsData from "./pages/companies/companiesDetailsData";
import CompaniesDetailsDataNew from "./pages/companies/companiesDetailsDataNew";
import CompaniesDataNew from "./pages/companies/companiesDataNew";

import Pricing from './pages/pricing';
import Ranking from './pages/rankings';
import UsersList from './pages/users/UserList';
import MyMarkets from './pages/rankSummary/myMarkets';
import UserOverview from './pages/users/UserOverview'
import ManageNotifications from './pages/notifications/manage';
import { UserProfile } from './pages/users/profile';
import Settings from './pages/settings';
import TemplateStudio from './pages/settings/templateStudio';
import Lookup from './pages/settings/lookup';
import { lookupTables } from './pages/settings/lookup/lookupTables';
import NotAuthorised from './pages/error/NotAuthorised';
import { setTokenGetter } from './utils/requests';
import loader from './assets/images/datahub-loader.svg'
import RankingSummary from './pages/rankSummary';
import customPricing from './pages/customPricing';
import ECLContent from './pages/ecl/index';
import ESIContent from './pages/esiId';
import HURequest from './pages/huRequest/index';
import HURequestDetail from "./pages/huRequest/HURequestDetail";

import ComplaintsOverview from './pages/complaints/overview';
import ComplaintsPeers from './pages/complaints/peers';
import ComplaintIntelligence from './pages/complaints/intelligence';
import ContractAI from "./pages/contractAI";
import BillAI from "./pages/billAI";
import CompanyAlias from "./pages/dataManagement/companyAlias";
import Brand from "./pages/dataManagement/brand";
import RateClass from "./pages/rateClass";

import Utilities from "./pages/utilities";
import SubscriptionsTab from "./pages/settings/components/subscriptions";
import UserList from "./pages/users/UserList";
import SystemAdminSettings from "./pages/settings/systemadmin/SystemAdminSettings";
import ExcludedDomains from "./pages/settings/components/excludedDomains";
import RouteTracker from "./analytics/RouteTracker";
import { initAnalytics } from "./analytics/analytics";

const App = () => {
    const { isLoading, error, logout, getAccessTokenSilently } = useAuth();
    const showNotAuthorised =
        error?.error === "access_denied" || error?.message === "Invalid state";
    useEffect(() => {
        initAnalytics();
    }, []);
    useEffect(() => {
        setTokenGetter(() => getAccessTokenSilently());
    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (error && !showNotAuthorised) {
            logout({ returnTo: window.location.origin });
        }
    }, [error, showNotAuthorised, logout]);

    if (error) {
        return showNotAuthorised ? <NotAuthorised /> : null;
    }

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <img src={loader} height={"100px"} alt="Loading" />
            </div>
        );
    }

    return (
        <SnackbarProvider
            Components={{
                json: JsonSnackbar,
            }}
            maxSnack={6}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
        >
            <HistoryRouter history={history}>
                <RouteTracker />
                <Routes>
                    <Route element={<UnifiedAppLayout />}>
                        <Route
                            path="/"
                            element={
                                <PrivateRoute
                                    component={DashboardPage}
                                    ModuleAccess="license"
                                />
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <PrivateRoute
                                    component={RankingSummary}
                                    SystemAdminAccessOnly
                                />
                            }
                        />
                        {/* <Route
                        path="/licenses/browse"
                        element={
                            <PrivateRoute
                                component={LicensedCompanies}
                                ModuleAccess="license"
                            />
                        }
                    />
                    <Route
                        path="/licenses/rawData"
                        element={
                            <PrivateRoute
                                component={licensesRawData}
                                ModuleAccess="license"
                            />
                        }
                    />
                    <Route
                        path="/offers/rawData"
                        element={<PrivateRoute component={offersRawData} />}
                    /> */}
                        {/* Companies */}
                        <Route
                            path="/companies"
                            element={
                                <PrivateRoute
                                    component={CompaniesData}
                                    ModuleAccess="license"
                                />
                            }
                        />
                        <Route
                            path="/companies/companiesDataNew"
                            element={
                                <PrivateRoute
                                    component={CompaniesDataNew}
                                    ModuleAccess="license"
                                />
                            }
                        />
                        <Route
                            path="/companies/companiesDetailsData/:companyId/:companyName"
                            element={
                                <PrivateRoute
                                    component={CompaniesDetailsData}
                                    ModuleAccess="license"
                                />
                            }
                        />
                        <Route
                            path="/companies/:companyId"
                            element={
                                <PrivateRoute
                                    component={CompaniesDetailsDataNew}
                                    ModuleAccess="license"
                                />
                            }
                        />
                        {/* Notification */}
                        <Route
                            path="/notifications"
                            element={
                                <PrivateRoute component={ManageNotifications} />
                            }
                        />
                        <Route
                            path="/Profile"
                            element={<PrivateRoute component={UserProfile} />}
                        />

                        {/* Pricing */}
                        <Route
                            path="/pricing/matrix"
                            element={
                                <PrivateRoute
                                    component={Pricing}
                                    ModuleAccess="pricing"
                                />
                            }
                        />
                        {/* customPricing */}
                        <Route
                            path="/pricing/custom"
                            element={
                                <PrivateRoute
                                    component={customPricing}
                                    ModuleAccess="pricing"
                                />
                            }
                        />

                        {/* Rankings */}
                        <Route
                            path="/pricing/ranking"
                            element={
                                <PrivateRoute
                                    component={Ranking}
                                    ModuleAccess="pricing"
                                />
                            }
                        />

                        {/* Settings */}
                        <Route
                            path="/settings/users/:userId/overview"
                            element={
                                <PrivateRoute
                                    component={UserOverview}
                                    AdminAccessOnly
                                />
                            }
                        />

                        <Route
                            path="/settings"
                            element={<PrivateRoute component={Settings} />}
                        >
                            {/* //<Route index element={<PrivateRoute component={<Navigate to="subscriptions" replace />} />} /> */}
                            <Route path="template-studio" element={<TemplateStudio />} />
                            <Route path="lookup" element={<PrivateRoute component={Lookup} SystemAdminAccessOnly />}>
                                <Route index element={<Navigate to={lookupTables[0].id} replace />} />
                                {lookupTables.filter(definition => !definition.disabled).map((definition) => (
                                    <Route
                                        key={definition.id}
                                        path={definition.id}
                                        element={<definition.Component key={definition.id} definition={definition} />}
                                    />
                                ))}
                                <Route path="*" element={<Navigate to={`/settings/lookup/${lookupTables[0].id}`} replace />} />
                            </Route>
                            <Route
                                path="general"
                                element={
                                    <>
                                        <br />
                                        <br />
                                        <center>
                                            <h3>Coming Soon!</h3>
                                        </center>
                                    </>
                                }
                            />
                            <Route
                                path="subscriptions"
                                element={<SubscriptionsTab />}
                            />
                            <Route
                                path="users"
                                element={
                                    <PrivateRoute
                                        component={UserList}
                                        AdminAccessOnly
                                    />
                                }
                            />
                            <Route
                                path="systemadmin"
                                element={
                                    <PrivateRoute
                                        component={SystemAdminSettings}
                                        SystemAdminAccessOnly
                                    />
                                }
                            />
                            <Route
                                path="excluded-domains"
                                element={
                                    <PrivateRoute
                                        component={ExcludedDomains}
                                        SystemAdminAccessOnly
                                    />
                                }
                            />
                        </Route>
                        <Route
                            path="/account/ecl"
                            element={
                                <PrivateRoute
                                    component={ECLContent}
                                    ModuleAccess="ecl"
                                />
                            }
                        />
                        <Route
                            path="/account/esiid"
                            element={
                                <PrivateRoute
                                    component={ESIContent}
                                    ModuleAccess="esiid"
                                />
                            }
                        />
                        <Route
                            path="/account/hu-request"
                            element={
                                <PrivateRoute
                                    component={HURequest}
                                    ModuleAccess="huRequest"
                                    SystemAdminAccessOnly
                                />
                            }
                        />
                        <Route
                            path="/account/hu-request/:id"
                            element={
                                <PrivateRoute
                                    component={HURequestDetail}
                                    ModuleAccess="huRequest"
                                    SystemAdminAccessOnly
                                />
                            }
                        />
                        <Route
                            path="/ai/contract"
                            element={
                                <PrivateRoute
                                    component={ContractAI}
                                    ModuleAccess="contractAI"
                                    SystemAdminAccessOnly

                                />
                            }

                        />
                        <Route
                            path="/ai/bill"
                            element={
                                <PrivateRoute
                                    component={BillAI}
                                    ModuleAccess="billAI"
                                    SystemAdminAccessOnly
                                />
                            }

                        />
                        <Route path="/complaints/overview" element={<PrivateRoute component={ComplaintsOverview} SystemAdminAccessOnly />} />
                        <Route path="/complaints/peers" element={<PrivateRoute component={ComplaintsPeers} SystemAdminAccessOnly />} />
                        <Route path="/complaints/intelligence" element={<PrivateRoute component={ComplaintIntelligence} SystemAdminAccessOnly />} />
                        <Route path="/data-management/company-alias" element={<PrivateRoute component={CompanyAlias} SystemAdminAccessOnly />} />
                        <Route path="/data-management/brand" element={<PrivateRoute component={Brand} SystemAdminAccessOnly />} />
                        <Route path="/data-management/rate-class" element={<PrivateRoute component={RateClass} />} />

                        <Route path="/utilities" element={<PrivateRoute component={Utilities} SystemAdminAccessOnly />} />
                        <Route path="/utilities/:id" element={<PrivateRoute component={Utilities} SystemAdminAccessOnly />} />
                    </Route>
                    <Route path="/account/logout" element={<LogoutPage />} />
                </Routes>
            </HistoryRouter>
        </SnackbarProvider>
    );
};

export default App;
